import { useMemo, useState } from 'react';
import { ILessonVideo } from '@/shared/types/lesson';

interface ILessonVideoProps {
  video: ILessonVideo;
  autoPlayOnClick?: boolean;
}

export const LessonVideo = ({ video, autoPlayOnClick = true }: ILessonVideoProps) => {
  // Плеер держит адрес, с которым запущен: ссылки, обновлённые по urlExpiresAt, его не
  // перезагружают. Другое видео (переход к другому уроку) открывается с обложки
  const [player, setPlayer] = useState<{ videoId: number; src: string } | null>(null);
  if (player && player.videoId !== video.id) {
    setPlayer(null);
  }
  const playingSrc = player?.videoId === video.id ? player.src : null;

  const iframeSrc = useMemo(() => {
    if (!autoPlayOnClick) return video.embedUrl;

    const url = new URL(video.embedUrl);
    url.searchParams.set('autoplay', 'true');
    url.searchParams.set('muted', 'true');
    url.searchParams.set('controls', 'true');
    return url.toString();
  }, [video.embedUrl, autoPlayOnClick]);

  return (
    <div className="relative w-full aspect-video overflow-hidden rounded-2xl bg-black">
      {!playingSrc ? (
        <button
          type="button"
          onClick={() => setPlayer({ videoId: video.id, src: iframeSrc })}
          className="group absolute inset-0 w-full h-full cursor-pointer"
          aria-label={`Play video${video.name ? `: ${video.name}` : ''}`}
        >
          {video.thumbnailUrl ? (
            <img
              src={video.thumbnailUrl}
              alt={video.name ?? 'Video thumbnail'}
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
              draggable={false}
            />
          ) : (
            <div className="absolute inset-0 bg-neutral-900" />
          )}

          <div className="absolute inset-0 bg-black/20 transition-colors duration-500 group-hover:bg-black/10" />

          <div className="absolute inset-0 flex items-center justify-center">
            <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-red-600/95 shadow-lg transition-transform duration-500 group-hover:scale-105">
              <svg width="36" height="36" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="white" d="M8 5v14l11-7z" />
              </svg>
            </span>
          </div>
        </button>
      ) : (
        <iframe
          src={playingSrc}
          className="absolute inset-0 w-full h-full"
          allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
          allowFullScreen
          title={video.name ?? ''}
          loading="lazy"
          referrerPolicy="strict-origin-when-cross-origin"
        />
      )}
    </div>
  );
};
